'use client';
import * as React from 'react';
import invariant from 'invariant';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import dayjs, { Dayjs } from 'dayjs';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormGroup from '@mui/material/FormGroup';
import Typography from '@mui/material/Typography';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { GridSingleSelectColDef } from '@mui/x-data-grid';
import FormControlLabel from '@mui/material/FormControlLabel';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import Checkbox, { CheckboxProps } from '@mui/material/Checkbox';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers/DatePicker';
import Select, { SelectProps, SelectChangeEvent } from '@mui/material/Select';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  DateTimePicker,
  DateTimePickerProps,
} from '@mui/x-date-pickers/DateTimePicker';

import { CrudContext } from '../shared/context';

import { groupFieldsBySections } from './sectioning';
import {
  OmitId,
  DataField,
  DataModel,
  DataSource,
  DataFieldFormValue,
} from './types';

interface CrudFormState<D extends DataModel> {
  values: Partial<OmitId<D>>;
  errors: Partial<Record<keyof D, string>>;
}

export interface CrudFormSlotProps {
  select?: SelectProps;
  checkbox?: CheckboxProps;
  textField?: TextFieldProps;
  datePicker?: DatePickerProps;
  dateTimePicker?: DateTimePickerProps;
}

export interface CrudFormSlots {
  /**
   * The select component used in the form.
   * @default Select
   */
  select: React.JSXElementConstructor<SelectProps>;
  /**
   * The checkbox component used in the form.
   * @default TextField
   */
  checkbox?: React.JSXElementConstructor<CheckboxProps>;
  /**
   * The text field component used in the form.
   * @default TextField
   */
  textField?: React.JSXElementConstructor<TextFieldProps>;
  /**
   * The date picker component used in the form.
   * @default DatePicker
   */
  datePicker?: React.JSXElementConstructor<DatePickerProps>;
  /**
   * The date and time picker component used in the form.
   * @default DatePicker
   */
  dateTimePicker?: React.JSXElementConstructor<DateTimePickerProps>;
}

export interface CrudFormProps<D extends DataModel> {
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots?: CrudFormSlots;
  /**
   * Text for form submit button.
   */
  submitButtonLabel: string;
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource?: DataSource<D>;
  /**
   * Form state object, including field values and errors.
   */
  formState: CrudFormState<D>;
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps?: CrudFormSlotProps;
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: (formValues: Partial<OmitId<D>>) => Promise<void> | void;
  /**
   * Callback fired when the form is reset.
   */
  onReset?: (formValues: Partial<OmitId<D>>) => Promise<void> | void;
  /**
   * Callback fired when a form field is changed.
   */
  onFieldChange: (
    name: keyof D,
    value: DataFieldFormValue,
    updates?: Record<string, DataFieldFormValue>
  ) => Promise<void> | void;
}

/**
 *
 * Demos:
 *
 * - [CRUD](https://mui.com/toolpad/core/react-crud/)
 *
 * API:
 *
 * - [CrudForm API](https://mui.com/toolpad/core/api/crud-form)
 */
function CrudForm<D extends DataModel>(props: CrudFormProps<D>) {
  const {
    slots,
    onReset,
    onSubmit,
    formState,
    slotProps,
    onFieldChange,
    submitButtonLabel,
  } = props;

  const formValues = formState.values;
  const formErrors = formState.errors;

  const crudContext = React.useContext(CrudContext);
  const dataSource = (props.dataSource ??
    crudContext.dataSource) as NonNullable<typeof props.dataSource>;

  invariant(dataSource, 'No data source found.');

  const { fields } = dataSource;

  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = React.useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      setIsSubmitting(true);
      try {
        await onSubmit(formValues);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formValues, onSubmit]
  );

  const handleTextFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(event.target.name, event.target.value);
    },
    [onFieldChange]
  );

  const handleNumberFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onFieldChange(event.target.name, Number(event.target.value));
    },
    [onFieldChange]
  );

  const handleCheckboxFieldChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onFieldChange(event.target.name, checked);
    },
    [onFieldChange]
  );

  const handleDateFieldChange = React.useCallback(
    (name: string) => (value: Dayjs | null) => {
      if (value?.isValid()) {
        onFieldChange(name, value.toISOString() ?? null);
      } else if (formValues[name]) {
        onFieldChange(name, null);
      }
    },
    [formValues, onFieldChange]
  );

  const handleSelectFieldChange = React.useCallback(
    (event: SelectChangeEvent) => {
      onFieldChange(event.target.name, event.target.value);
    },
    [onFieldChange]
  );

  const renderField = React.useCallback(
    (formField: DataField, cssGrid = false) => {
      const {
        type,
        field,
        headerName,
        formGridSize,
        formGridRowSpan,
        renderFormField,
      } = formField;

      const fieldValue = formValues[field];
      const fieldError = formErrors[field];

      let fieldElement: React.ReactNode = null;

      if (renderFormField) {
        fieldElement = renderFormField({
          formValues,
          error: fieldError ?? null,
          value: (fieldValue ?? null) as DataFieldFormValue,
          onChange: (value, updates) => onFieldChange(field, value, updates),
        });
      } else if (!type || type === 'string') {
        const TextFieldComponent = slots?.textField ?? TextField;

        fieldElement = (
          <TextFieldComponent
            fullWidth
            name={field}
            label={headerName}
            error={!!fieldError}
            value={fieldValue ?? ''}
            helperText={fieldError ?? ' '}
            onChange={handleTextFieldChange}
            {...slotProps?.textField}
          />
        );
      } else if (type === 'number') {
        const TextFieldComponent = slots?.textField ?? TextField;

        fieldElement = (
          <TextFieldComponent
            fullWidth
            name={field}
            type="number"
            label={headerName}
            error={!!fieldError}
            value={fieldValue ?? ''}
            helperText={fieldError ?? ' '}
            onChange={handleNumberFieldChange}
            {...slotProps?.textField}
          />
        );
      } else if (type === 'boolean') {
        const CheckBoxComponent = slots?.checkbox ?? Checkbox;

        fieldElement = (
          <FormControl>
            <FormControlLabel
              name={field}
              label={headerName}
              control={
                <CheckBoxComponent
                  size="large"
                  checked={fieldValue as boolean}
                  onChange={handleCheckboxFieldChange}
                  {...slotProps?.checkbox}
                />
              }
            />
            <FormHelperText error={!!fieldError}>
              {fieldError ?? ' '}
            </FormHelperText>
          </FormControl>
        );
      } else if (type === 'date') {
        const DatePickerComponent = slots?.datePicker ?? DatePicker;

        fieldElement = (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePickerComponent
              name={field}
              label={headerName}
              onChange={handleDateFieldChange(field)}
              value={fieldValue ? dayjs(fieldValue as string) : null}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!fieldError,
                  helperText: fieldError ?? ' ',
                },
              }}
              {...slotProps?.datePicker}
            />
          </LocalizationProvider>
        );
      } else if (type === 'dateTime') {
        const DateTimePickerComponent = slots?.dateTimePicker ?? DateTimePicker;

        fieldElement = (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePickerComponent
              name={field}
              label={headerName}
              onChange={handleDateFieldChange(field)}
              value={fieldValue ? dayjs(fieldValue as string) : null}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!fieldError,
                  helperText: fieldError ?? ' ',
                },
              }}
              {...slotProps?.dateTimePicker}
            />
          </LocalizationProvider>
        );
      } else if (type === 'singleSelect') {
        const SelectComponent = slots?.select ?? Select;

        const { valueOptions, getOptionValue, getOptionLabel } =
          formField as GridSingleSelectColDef;

        if (valueOptions && Array.isArray(valueOptions)) {
          const labelId = `${field}-label`;

          fieldElement = (
            <FormControl fullWidth error={!!fieldError}>
              <InputLabel id={labelId}>{headerName}</InputLabel>
              <SelectComponent
                fullWidth
                name={field}
                defaultValue=""
                labelId={labelId}
                label={headerName}
                value={(fieldValue as string) ?? ''}
                onChange={handleSelectFieldChange as SelectProps['onChange']}
                {...slotProps?.select}
              >
                {valueOptions.map((option) => {
                  let optionValue: string | number = option as string | number;
                  let optionLabel: string | number = option as string | number;
                  if (
                    typeof option !== 'string' &&
                    typeof option !== 'number'
                  ) {
                    optionValue = getOptionValue
                      ? getOptionValue(option)
                      : option.value;
                    optionLabel = getOptionLabel
                      ? getOptionLabel(option)
                      : option.label;
                  }

                  return (
                    <MenuItem key={optionValue} value={optionValue}>
                      {optionLabel}
                    </MenuItem>
                  );
                })}
              </SelectComponent>
              <FormHelperText>{fieldError ?? ' '}</FormHelperText>
            </FormControl>
          );
        }
      }

      if (cssGrid)
        return (
          <Box
            key={field}
            sx={{
              minWidth: 0,
              display: 'flex',
              gridRow: {
                xs: 'auto',
                sm: formGridRowSpan ? `span ${formGridRowSpan}` : 'auto',
              },
            }}
          >
            {fieldElement}
          </Box>
        );
      return (
        <Grid
          key={field}
          sx={{ display: 'flex' }}
          size={formGridSize ?? { sm: 6, xs: 12 }}
        >
          {fieldElement}
        </Grid>
      );
    },
    [
      formErrors,
      formValues,
      handleCheckboxFieldChange,
      handleDateFieldChange,
      handleNumberFieldChange,
      handleSelectFieldChange,
      handleTextFieldChange,
      onFieldChange,
      slotProps,
      slots,
    ]
  );

  const handleReset = React.useCallback(async () => {
    if (onReset) {
      await onReset(formValues);
    }
  }, [formValues, onReset]);

  const formFields = React.useMemo(
    () =>
      fields.filter(
        ({ field, editable }) => field !== 'id' && editable !== false
      ),
    [fields]
  );

  const { sectionedGroups, unsectionedFields } = React.useMemo(
    () => groupFieldsBySections(formFields),
    [formFields]
  );

  return (
    <Box
      noValidate
      component="form"
      autoComplete="off"
      onReset={handleReset}
      sx={{ width: '100%' }}
      onSubmit={handleSubmit}
    >
      <FormGroup>
        <Grid container spacing={2} sx={{ mb: 2, width: '100%' }}>
          {unsectionedFields.map((field) => renderField(field))}
          {sectionedGroups.map((section) => (
            <React.Fragment key={`section:${section.title}`}>
              <Grid size={{ xs: 12 }}>
                <Typography sx={{ mt: 1 }} variant="overline">
                  {section.title}
                </Typography>
              </Grid>
              {section.fields.some((field) => field.formGridRowSpan) ? (
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      gap: 2,
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        sm: 'repeat(2, minmax(0, 1fr))',
                      },
                    }}
                  >
                    {section.fields.map((field) => renderField(field, true))}
                  </Box>
                </Grid>
              ) : (
                section.fields.map((field) => renderField(field))
              )}
            </React.Fragment>
          ))}
        </Grid>
      </FormGroup>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          size="large"
          type="submit"
          variant="contained"
          loading={isSubmitting}
        >
          {submitButtonLabel}
        </Button>
      </Box>
    </Box>
  );
}

CrudForm.propTypes /* remove-proptypes */ = {
  /**
   * Callback fired when the form is reset.
   */
  onReset: PropTypes.func,
  // ┌────────────────────────────── Warning ──────────────────────────────┐
  // │ These PropTypes are generated from the TypeScript type definitions. │
  // │ To update them, edit the TypeScript types and run `pnpm proptypes`. │
  // └─────────────────────────────────────────────────────────────────────┘
  /**
   * Server-side [data source](https://mui.com/toolpad/core/react-crud/#data-sources).
   */
  dataSource: PropTypes.object,
  /**
   * Callback fired when the form is submitted.
   */
  onSubmit: PropTypes.func.isRequired,
  /**
   * Callback fired when a form field is changed.
   */
  onFieldChange: PropTypes.func.isRequired,
  /**
   * Text for form submit button.
   */
  submitButtonLabel: PropTypes.string.isRequired,
  /**
   * Form state object, including field values and errors.
   */
  formState: PropTypes.shape({
    errors: PropTypes.object.isRequired,
    values: PropTypes.object.isRequired,
  }).isRequired,
  /**
   * The props used for each slot inside.
   * @default {}
   */
  slotProps: PropTypes.shape({
    select: PropTypes.object,
    checkbox: PropTypes.object,
    textField: PropTypes.object,
    datePicker: PropTypes.object,
    dateTimePicker: PropTypes.object,
  }),
  /**
   * The components used for each slot inside.
   * @default {}
   */
  slots: PropTypes.shape({
    select: PropTypes.elementType,
    checkbox: PropTypes.elementType,
    textField: PropTypes.elementType,
    datePicker: PropTypes.elementType,
    dateTimePicker: PropTypes.elementType,
  }),
} as any;

export { CrudForm };
